import { Fragment, type ReactNode } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

type Props = {
  totalCards: number;
  checkedOutCards: number;
  availableCards: number | null;
  needsChargeCards?: number | null;
  checkedOutOnly: boolean;
  onToggleCheckedOutOnly: () => void;
  /** 貸出可能をクリックしたとき（フィルタ解除＋貸出可カードを選択） */
  onFocusAvailable?: () => void;
  /** 要チャージをクリックしたとき（フィルタ解除＋要チャージカードを選択） */
  onFocusNeedsCharge?: () => void;
  /** 少枚数で検索バーを出さないときの補助コントロール */
  compactExtras?: {
    showInactive: boolean;
    onShowInactiveChange: (value: boolean) => void;
    onReload: () => void;
    /** false のとき無効カード切替を隠す（無効カードが無い） */
    showInactiveToggle?: boolean;
  };
};

type StatSegProps = {
  label: string;
  value: number;
  valueClass: string;
  action?: {
    onClick: () => void;
    title: string;
    pressed?: boolean;
    activeClass?: string;
  };
};

function TppStatSegment({ label, value, valueClass, action }: StatSegProps) {
  const body = (
    <>
      <span className="small text-muted">{label}</span>
      <strong className={`fs-5 mb-0 ${valueClass}`}>{value}</strong>
    </>
  );
  if (!action) {
    return <div className="d-flex align-items-baseline gap-2">{body}</div>;
  }
  return (
    <button
      type="button"
      className={`btn btn-link text-decoration-none p-0 d-flex align-items-baseline gap-2 ${
        action.activeClass ?? ''
      }`}
      onClick={action.onClick}
      aria-pressed={action.pressed}
      title={action.title}
    >
      {body}
    </button>
  );
}

/** 登録・貸出中・貸出可能・要チャージの概況バー */
export default function TppCardPoolStats({
  totalCards,
  checkedOutCards,
  availableCards,
  needsChargeCards = null,
  checkedOutOnly,
  onToggleCheckedOutOnly,
  onFocusAvailable,
  onFocusNeedsCharge,
  compactExtras,
}: Props) {
  const { t } = useTranslation();
  if (totalCards <= 0) return null;

  const availableCount = availableCards ?? 0;
  const needsChargeCount = needsChargeCards ?? 0;
  const canFocusAvailable = !!onFocusAvailable && availableCount > 0;
  const canFocusNeedsCharge = !!onFocusNeedsCharge && needsChargeCount > 0;
  const canFilterCheckedOut = checkedOutCards > 0 && totalCards > 1;
  const showStatNumbers = totalCards > 1 || checkedOutOnly;
  const showTotalStat = totalCards > 1;
  const showCheckedOutStat = checkedOutCards > 0 || checkedOutOnly;
  const showAvailableStat =
    availableCount > 0 && (checkedOutCards > 0 || availableCount < totalCards);
  const showNeedsChargeStat = needsChargeCount > 0 && totalCards > 1;

  if (!showStatNumbers && !compactExtras) return null;

  const segments: ReactNode[] = [];
  if (showTotalStat) {
    segments.push(
      <TppStatSegment
        key="total"
        label={t('tpp.stats_total_cards')}
        value={totalCards}
        valueClass=""
      />
    );
  }
  if (showCheckedOutStat) {
    segments.push(
      <TppStatSegment
        key="out"
        label={t('tpp.stats_checked_out')}
        value={checkedOutCards}
        valueClass={canFilterCheckedOut ? 'text-warning-emphasis' : 'text-muted'}
        action={
          canFilterCheckedOut
            ? {
                onClick: onToggleCheckedOutOnly,
                title: t('tpp.stats_checked_out_filter_hint'),
                pressed: checkedOutOnly,
                activeClass: checkedOutOnly ? 'border-bottom border-warning border-2' : '',
              }
            : undefined
        }
      />
    );
  }
  if (showAvailableStat) {
    segments.push(
      <TppStatSegment
        key="avail"
        label={t('tpp.stats_available')}
        value={availableCount}
        valueClass={canFocusAvailable ? 'text-success' : 'text-muted'}
        action={
          canFocusAvailable
            ? {
                onClick: onFocusAvailable!,
                title: t('tpp.stats_available_focus_hint'),
              }
            : undefined
        }
      />
    );
  }
  if (showNeedsChargeStat) {
    segments.push(
      <TppStatSegment
        key="charge"
        label={t('tpp.stats_needs_charge')}
        value={needsChargeCount}
        valueClass="text-info"
        action={
          canFocusNeedsCharge
            ? {
                onClick: onFocusNeedsCharge!,
                title: t('tpp.stats_needs_charge_focus_hint'),
              }
            : undefined
        }
      />
    );
  }

  return (
    <div className="mb-3 bg-white rounded shadow-sm overflow-hidden">
      {showStatNumbers && segments.length > 0 && (
        <div
          className="d-flex flex-nowrap align-items-center gap-3 gap-md-4 px-3 py-2 overflow-auto"
          role="status"
          aria-label={t('tpp.stats_aria')}
        >
          {segments.map((seg, i) => (
            <Fragment key={i}>
              {i > 0 ? <div className="vr d-none d-sm-block" aria-hidden /> : null}
              {seg}
            </Fragment>
          ))}
          {checkedOutOnly && (
            <Button
              type="button"
              size="sm"
              variant="link"
              className="text-decoration-none ms-sm-auto px-0"
              onClick={onToggleCheckedOutOnly}
            >
              {t('tpp.stats_clear_checked_out_filter')}
            </Button>
          )}
        </div>
      )}
      {compactExtras && (
        <div
          className={`d-flex flex-wrap align-items-center gap-3 px-3 py-1 small${
            showStatNumbers ? ' border-top' : ''
          }`}
        >
          <Button
            size="sm"
            variant="link"
            className="text-decoration-none text-nowrap px-0"
            onClick={compactExtras.onReload}
            aria-label={t('common.reload')}
            title={t('common.reload')}
          >
            {compactExtras.showInactiveToggle === false ? (
              <i className="fas fa-sync-alt" aria-hidden />
            ) : (
              t('common.reload')
            )}
          </Button>
          {compactExtras.showInactiveToggle !== false ? (
            <Form.Check
              type="checkbox"
              id="tppShowInactive"
              className="mb-0"
              label={t('tpp.show_inactive_cards')}
              checked={compactExtras.showInactive}
              onChange={(e) => compactExtras.onShowInactiveChange(e.target.checked)}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}
